// src/components/Profile/NotificationItem.jsx
import React, { useState } from 'react';
import {
  Bell, Users, UserPlus, UserMinus, RefreshCw,
  Share2, CheckCircle, XCircle, Trash2,
} from 'lucide-react';
import { notificationsAPI, invitationsAPI } from '../../api/groupsApi';
import './NotificationItem.css';

const TYPE_ICONS = {
  group_invitation:   <UserPlus size={16} className="notif-icon notif-icon--invite" />,
  invitation_accepted:<CheckCircle size={16} className="notif-icon notif-icon--success" />,
  invitation_declined:<XCircle size={16} className="notif-icon notif-icon--warn" />,
  member_joined:      <Users size={16} className="notif-icon notif-icon--info" />,
  member_left:        <UserMinus size={16} className="notif-icon notif-icon--warn" />,
  role_changed:       <RefreshCw size={16} className="notif-icon notif-icon--info" />,
  removed_from_group: <UserMinus size={16} className="notif-icon notif-icon--danger" />,
  resource_shared:    <Share2 size={16} className="notif-icon notif-icon--info" />,
  system:             <Bell size={16} className="notif-icon notif-icon--default" />,
};

const NotificationItem = ({ notification, onUpdate, onDelete }) => {
  const [actionLoading, setActionLoading] = useState(null);

  const isInvite = notification.notification_type === 'group_invitation';
  const inv = notification.invitation;
  const isPending = inv?.status === 'pending';

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const handleAccept = async () => {
    setActionLoading('accept');
    try {
      await invitationsAPI.accept(inv.id);
      await notificationsAPI.markRead(notification.id);
      onUpdate({ ...notification, is_read: true, invitation: { ...inv, status: 'accepted' } });
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async () => {
    setActionLoading('decline');
    try {
      await invitationsAPI.decline(inv.id);
      await notificationsAPI.markRead(notification.id);
      onUpdate({ ...notification, is_read: true, invitation: { ...inv, status: 'declined' } });
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkRead = async () => {
    if (notification.is_read) return;
    try {
      await notificationsAPI.markRead(notification.id);
      onUpdate({ ...notification, is_read: true });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async () => {
    try {
      await notificationsAPI.delete(notification.id);
      onDelete(notification.id);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div
      className={`notification-item ${!notification.is_read ? 'notification-item--unread' : ''}`}
      onClick={handleMarkRead}
    >
      <div className="notification-item__icon">
        {TYPE_ICONS[notification.notification_type] || TYPE_ICONS.system}
      </div>

      <div className="notification-item__content">
        <p className="notification-item__title">{notification.title}</p>
        <p className="notification-item__message">{notification.message}</p>

        {/* Invitation action buttons */}
        {isInvite && inv && isPending && (
          <div className="notification-item__actions" onClick={(e) => e.stopPropagation()}>
            <button
              className="btn btn--sm btn--primary"
              onClick={handleAccept}
              disabled={!!actionLoading}
            >
              {actionLoading === 'accept' ? '…' : '✓ Accept'}
            </button>
            <button
              className="btn btn--sm btn--ghost btn--danger"
              onClick={handleDecline}
              disabled={!!actionLoading}
            >
              {actionLoading === 'decline' ? '…' : '✕ Decline'}
            </button>
          </div>
        )}

        {isInvite && inv && !isPending && (
          <span className={`invite-status invite-status--${inv.status}`}>
            {inv.status === 'accepted' ? '✓ Accepted' : '✕ Declined'}
          </span>
        )}

        <span className="notification-item__time">{timeAgo(notification.created_at)}</span>
      </div>

      <button
        className="notification-item__delete icon-btn"
        onClick={(e) => { e.stopPropagation(); handleDelete(); }}
        title="Delete notification"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
};

export default NotificationItem;
